
import React, { useState, useRef, useEffect } from 'react';
import { Card, Button } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Upload, FileText, RefreshCw, Trash2, ShieldAlert } from 'lucide-react';
import { API_BASE_URL } from '../config';

export const TelemetryToolAdmin: React.FC = () => {
    const { user } = useAuth();
    const [file, setFile] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [existingFiles, setExistingFiles] = useState<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const loadFiles = async () => {
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            // FIX: Removed /php/
            const res = await fetch(`${baseUrl}/tools.php`);
            const json = await res.json();
            if (json.status === 'success') setExistingFiles(json.data);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { loadFiles(); }, []);

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('tool_file', file);
        formData.append('description', description); // Send description

        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            // FIX: Removed /php/
            const res = await fetch(`${baseUrl}/tools.php`, { method: 'POST', body: formData });
            const json = await res.json();
            if (json.status === 'success') {
                alert("Upload erfolgreich!");
                setFile(null);
                setDescription('');
                loadFiles();
            } else alert("Fehler: " + json.message);
        } catch (e: any) { alert("Fehler: " + e.message); }
        finally { setUploading(false); }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <h1 className="text-2xl font-bold uppercase italic text-f1-red">Telemetrie-Tool hochladen</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card title="Upload Tool">
                    <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 flex flex-col items-center justify-center text-center">
                            <Upload className="w-12 h-12 text-gray-600 mb-4" />
                            <input type="file" ref={fileInputRef} className="hidden" accept=".zip,.exe,.py" onChange={e => setFile(e.target.files?.[0] || null)} />
                            {file ? <div className="text-white font-bold mb-4">{file.name}</div> : <p className="text-gray-500 mb-4">Wähle eine .zip, .exe oder .py Datei aus.</p>}
                            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>Datei wählen</Button>
                        </div>
                        
                        <div>
                            <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Beschreibung / Anleitung</label>
                            <textarea 
                                className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white h-24 text-sm focus:border-f1-red outline-none"
                                placeholder="z.B. Entpacken und setup.exe starten. Benötigt Python 3.10."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>

                        {file && <Button className="w-full justify-center" onClick={handleUpload} disabled={uploading}>{uploading ? <RefreshCw className="animate-spin mr-2"/> : <Upload className="mr-2"/>} Hochladen</Button>}
                    </div>
                </Card>
                <Card title="Dateien auf Server">
                    <div className="space-y-2">
                        {existingFiles.length === 0 && <p className="text-gray-600 italic">Keine Dateien vorhanden.</p>}
                        {existingFiles.map(f => (
                            <div key={f.name} className="p-3 bg-gray-900 border border-gray-800 rounded">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-3">
                                        <FileText className="text-blue-500" size={18}/>
                                        <div className="text-sm font-bold text-white">{f.name}</div>
                                    </div>
                                    <div className="text-[10px] text-gray-500">{(f.size / 1024 / 1024).toFixed(2)} MB</div>
                                </div>
                                {f.description && <div className="text-xs text-gray-400 italic mb-2 bg-black/20 p-2 rounded">{f.description}</div>}
                                <div className="text-[10px] text-gray-600 text-right">{new Date(f.date * 1000).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};
